import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Search, Plus, Check } from 'lucide-react';
import {
  SELECTION_CATEGORY_LIBRARY,
  searchCategories,
  getAllTags,
  toSelectionCategoryConfig,
  type SelectionCategoryDefinition,
} from '@/components/widget/config/selectionCategoryLibrary';
import type { SelectionCategoryConfig } from '@/types/firestore';

interface SelectionCategoryPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (category: SelectionCategoryConfig) => void;
  excludeIds?: string[]; // Categories already added (to show as disabled)
}

export function SelectionCategoryPicker({
  open,
  onOpenChange,
  onSelect,
  excludeIds = [],
}: SelectionCategoryPickerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [previewCategory, setPreviewCategory] = useState<SelectionCategoryDefinition | null>(null);

  const allTags = useMemo(() => getAllTags(), []);

  const filteredCategories = useMemo(() => {
    return searchCategories(searchQuery, selectedTag ? [selectedTag] : undefined);
  }, [searchQuery, selectedTag]);

  const handleSelectCategory = (category: SelectionCategoryDefinition) => {
    onSelect(toSelectionCategoryConfig(category));
    onOpenChange(false);
    setSearchQuery('');
    setSelectedTag(null);
    setPreviewCategory(null);
  };

  const handleClose = () => {
    onOpenChange(false);
    setSearchQuery('');
    setSelectedTag(null);
    setPreviewCategory(null);
  };

  // Group categories for display
  const groupedCategories = useMemo(() => {
    const groups: Record<string, SelectionCategoryDefinition[]> = {};
    filteredCategories.forEach((cat) => {
      const group = cat.tags[0] || 'other';
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(cat);
    });
    return groups;
  }, [filteredCategories]);

  const tagLabels: Record<string, string> = {
    general: 'General',
    kitchen: 'Kitchen',
    bathroom: 'Bathroom',
    outdoor: 'Outdoor',
    custom: 'Custom',
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Add Selection Category</DialogTitle>
          <DialogDescription>
            Choose a category from the library or search for specific options
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 flex-1 min-h-0">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Tag filters */}
          <div className="flex flex-wrap gap-2">
            <Badge
              variant={selectedTag === null ? 'default' : 'outline'}
              className="cursor-pointer"
              onClick={() => setSelectedTag(null)}
            >
              All
            </Badge>
            {allTags.slice(0, 10).map((tag) => (
              <Badge
                key={tag}
                variant={selectedTag === tag ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
              >
                {tagLabels[tag] || tag}
              </Badge>
            ))}
          </div>

          {/* Category list */}
          <ScrollArea className="flex-1 -mx-6 px-6">
            <div className="space-y-6 pr-4">
              {Object.entries(groupedCategories).map(([group, categories]) => (
                <div key={group}>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2 capitalize">
                    {tagLabels[group] || group}
                  </h4>
                  <div className="grid gap-2">
                    {categories.map((category) => {
                      const isExcluded = excludeIds.includes(category.id);
                      return (
                        <div
                          key={category.id}
                          className={`flex items-start gap-3 p-3 rounded-lg border transition-colors group ${
                            isExcluded
                              ? 'bg-muted/50 opacity-50 cursor-not-allowed'
                              : 'hover:bg-accent hover:border-accent-foreground/20 cursor-pointer'
                          } ${
                            previewCategory?.id === category.id
                              ? 'bg-accent border-primary'
                              : ''
                          }`}
                          onClick={() => !isExcluded && setPreviewCategory(category)}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium">{category.label}</span>
                              <Badge variant="outline" className="text-[10px]">
                                {category.defaultOptions.length} options
                              </Badge>
                              {category.allowColor && (
                                <Badge variant="secondary" className="text-[10px]">
                                  Color
                                </Badge>
                              )}
                              {isExcluded && (
                                <Badge variant="secondary" className="text-[10px]">
                                  <Check className="h-3 w-3 mr-1" />
                                  Added
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground group-hover:text-foreground/70 mt-0.5 transition-colors">
                              {category.description}
                            </p>
                          </div>
                          {!isExcluded && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectCategory(category);
                              }}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {filteredCategories.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No categories found matching your search
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Preview panel */}
          {previewCategory && (
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium mb-2">
                Preview: {previewCategory.label}
              </h4>
              <div className="flex flex-wrap gap-2">
                {previewCategory.defaultOptions.map((opt) => (
                  <Badge key={opt.id} variant="outline" className="text-xs">
                    {opt.label}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          {previewCategory && !excludeIds.includes(previewCategory.id) && (
            <Button onClick={() => handleSelectCategory(previewCategory)}>
              Add {previewCategory.label}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
